import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';

import { GetPropertyByIdQuery } from 'gql/generated.graphql';

const SALE_TEAMS_FLAT_FIELDS_TO_COMPARE = [
  'saleDirectorId',
  'commission',
  'contactUrl',
];

const customEqualComparison = (
  propValue: any,
  stateValue: any
): boolean | undefined => {
  if (!propValue && !stateValue) return true;
};

const checkSaleTeamsValuesIsEqual = (
  propSalesTeams: any = [],
  stateSaleTeams: any = []
) => {
  // remove empty teams
  const stateTeams = stateSaleTeams.filter((doc: any) => !!doc.saleTeamId);

  // quick compare
  if (propSalesTeams.length !== stateTeams.length) return false;

  // deep compare
  // build state sales team map by saleTeamId
  const stateSalesTeamsMapBySaleTeamId = stateSaleTeams.reduce(
    (memo: any, { saleTeamId, ...restDoc }: any) => ({
      ...memo,
      [saleTeamId]: restDoc,
    }),
    {}
  );
  return !propSalesTeams.some((propertySaleTeam: any) => {
    const { saleTeamId, saleAssociateIds = [] } = propertySaleTeam;
    const stateSaleTeam = stateSalesTeamsMapBySaleTeamId[saleTeamId];

    // quick compare, if property sale team wasn't found in state the return true
    if (!stateSaleTeam) return true;

    const { saleAssociateIds: stateSaleAssociateIds = [] } = stateSaleTeam;

    // quick compare, if sale associate length is not equal to state
    if (saleAssociateIds.length !== stateSaleAssociateIds.length) return true;

    // compare array ids
    if (!isEqual(sortBy(saleAssociateIds), sortBy(stateSaleAssociateIds)))
      return true;

    // compare rest fields
    return SALE_TEAMS_FLAT_FIELDS_TO_COMPARE.some((fieldToCompare) => {
      const oldValue = propertySaleTeam[fieldToCompare] || null;
      const newValue = stateSaleTeam[fieldToCompare] || null;

      return oldValue !== newValue;
    });
  });
};

const checkRangeValuesIsEqual = (
  propertyRange?: any,
  formRange?: { min: number; max: number }
) => {
  if (propertyRange?.min !== formRange?.min) return false;
  if (propertyRange?.max !== formRange?.max) return false;
  return true;
};

export const compareGeneralInfoData = (
  property: GetPropertyByIdQuery['getPropertyById'],
  formStateData: any
) => {
  const {
    errorSlug,
    isLoading,
    saleAssociate: formSaleAssociate,
    averageBathroomsNumber,
    averageBedroomsNumber,
    saleTeams,
    ...rest
  } = formStateData;

  const propertiesToPick = {
    name: property.name,
    primaryOrganization: property?.primaryOrganization?._id,
    organizations: property.organizations?.map((org) => org._id),
    market: property?.marketArea?.name,
    prices: {
      min: property.priceRange?.min,
      max: property.priceRange?.max,
    },
    website: property.website,
    county: property.location.address?.county,
    state: property.location.address?.state,
    city: property.location.address?.city,
    street: property.location.address?.street,
    zipCode: property.location.address?.zipCode,
    mainPropertyMediaId: property.mainPropertyMediaId,
    defaultInventoryDetails: property.defaultInventoryDetails,
    place: {
      place_id: property.location.googlePlaceId,
      lat: property.location.geoLocation?.coordinates[1],
      lng: property.location.geoLocation?.coordinates[0],
    },
  };

  const fields = Object.keys(rest);

  const propertyValues = pick(propertiesToPick, fields);
  const formState = pick(formStateData, fields);

  return (
    isEqualWith(propertyValues, formState, customEqualComparison) &&
    checkSaleTeamsValuesIsEqual(property.saleTeams, saleTeams) &&
    checkRangeValuesIsEqual(
      property.averageBedroomsNumber,
      averageBedroomsNumber
    ) &&
    checkRangeValuesIsEqual(
      property.averageBathroomsNumber,
      averageBathroomsNumber
    )
  );
};
